class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");
    this.header = document.querySelector("sticky-header");
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", this.close.bind(this)),
    );

    // Protect cart icon from being replaced
    this.initCartIconProtection();
  }

  initCartIconProtection() {
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (!cartIconBubble) return;

    // Watch for changes to cart icon bubble
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Check if icon was removed
          const hasIcon = cartIconBubble.querySelector(
            ".fas.fa-shopping-basket",
          );
          if (!hasIcon) {
            // Restore the icon
            const icon = document.createElement("i");
            icon.className = "fas fa-shopping-basket";
            cartIconBubble.insertBefore(icon, cartIconBubble.firstChild);
          }
        }
      });
    });

    observer.observe(cartIconBubble, {
      childList: true,
      subtree: false,
    });
  }

  open() {
    this.notification.classList.add("animate", "active");

    this.notification.addEventListener(
      "transitionend",
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    this.notification.classList.remove("active");
    document.body.removeEventListener("click", this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    this.getSectionsToRender().forEach((section) => {
      if (section.id === "cart-icon-bubble") {
        // Custom handling for cart icon - fetch cart to get item count
        fetch("/cart.js")
          .then((response) => response.json())
          .then((cart) => {
            this.updateCartCounter(cart.item_count);
          })
          .catch(() => {
            // Fallback - try to get from parsedState or count from response
            const itemCount =
              parsedState.item_count || parsedState.items?.length || 1;
            this.updateCartCounter(itemCount);
          });
      } else {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector,
          );
      }
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  updateCartCounter(itemCount) {
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (!cartIconBubble) return;

    // Ensure the icon exists at the beginning
    let icon = cartIconBubble.querySelector(".fas.fa-shopping-basket");
    if (!icon) {
      icon = document.createElement("i");
      icon.className = "fas fa-shopping-basket";
      cartIconBubble.insertBefore(icon, cartIconBubble.firstChild);
    }

    // Ensure main visually-hidden span exists (not inside cart-count-bubble)
    let mainVisuallyHidden = null;
    const visuallyHiddenSpans =
      cartIconBubble.querySelectorAll(".visually-hidden");
    visuallyHiddenSpans.forEach((span) => {
      if (!span.closest(".cart-count-bubble")) {
        mainVisuallyHidden = span;
      }
    });

    if (!mainVisuallyHidden) {
      mainVisuallyHidden = document.createElement("span");
      mainVisuallyHidden.className = "visually-hidden";
      mainVisuallyHidden.textContent = "Cart";
      cartIconBubble.appendChild(mainVisuallyHidden);
    }

    // Handle the counter bubble
    let counterBubble = cartIconBubble.querySelector(".cart-count-bubble");

    if (itemCount > 0) {
      if (!counterBubble) {
        // Create new counter bubble
        counterBubble = document.createElement("div");
        counterBubble.className = "cart-count-bubble";
        cartIconBubble.appendChild(counterBubble);
      }

      // Update counter content
      counterBubble.innerHTML = `
        <span aria-hidden="true">${itemCount}</span>
        <span class="visually-hidden">${itemCount} item${itemCount !== 1 ? "s" : ""}</span>
      `;
    } else if (counterBubble) {
      // Remove counter if cart is empty
      counterBubble.remove();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-notification-product",
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: "cart-notification-button",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest("cart-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure
        ? disclosure.querySelector("summary")
        : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-notification", CartNotification);
